import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../provider/auth';
import { useApi } from '../../api/api';
import { get_user_info } from '../../api/functions';
import './Header.css';

const Header = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, isLoading, user, login, logout, token } = useAuth();
  const getUserInfoApi = useApi(get_user_info);
  const getUserInfoApiRef = useRef(getUserInfoApi);
  getUserInfoApiRef.current = getUserInfoApi;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const [visibleCount, setVisibleCount] = useState(null);
  const [isMobileMode, setIsMobileMode] = useState(window.innerWidth <= 1023);
  const [infoPages, setInfoPages] = useState([]);
  const location = useLocation();
  const langRef = useRef(null);
  const navRef = useRef(null);
  const moreRef = useRef(null);
  const moreButtonRef = useRef(null);
  const navItemsRef = useRef([]);
  const headerContainerRef = useRef(null);

  const getLocalizedField = useCallback((item, fieldPrefix) => {
    if (!item) return '';
    const language = i18n.language === 'cs' ? 'cz' : i18n.language;
    if (item[`${fieldPrefix}_${language}`]) {
      return item[`${fieldPrefix}_${language}`];
    }
    if (item[`${fieldPrefix}_cz`]) {
      return item[`${fieldPrefix}_cz`];
    }
    if (item[`${fieldPrefix}_en`]) {
      return item[`${fieldPrefix}_en`];
    }
    if (item[`${fieldPrefix}_de`]) {
      return item[`${fieldPrefix}_de`];
    }
    return '';
  }, [i18n.language]);

  const navItems = useMemo(() => {
    const staticNavItems = [
      { path: '/news', label: t('header.news'), key: 'news' },
      { path: '/events', label: t('header.events'), key: 'events' }
    ];

    const pageNavItems = infoPages.map(page => ({
      path: `/page/${page.id}`,
      label: getLocalizedField(page, 'title') || 'Untitled Page',
      key: `page-${page.id}`
    }));

    return [...staticNavItems, ...pageNavItems];
  }, [t, infoPages, getLocalizedField]);

  useEffect(() => {
    const fetchPages = async () => {
      try {
        const userInfo = await getUserInfoApiRef.current.execute();
        const isMember = userInfo?.is_member || false;

        const headers = {};
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/page?skip=0&limit=100&show_private=${isMember}`,
          { headers }
        );

        if (response.ok) {
          const pages = await response.json();
          setInfoPages([...pages].reverse());
        } else {
          console.error('Failed to fetch pages:', response.status);
        }
      } catch (error) {
        console.error('Error fetching pages:', error);
      }
    };

    fetchPages();
  }, [token]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
    setIsLangOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleAuthClick = () => {
    if (isLoading) return;

    if (isAuthenticated) {
      logout();
    } else {
      login();
    }
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isMoreButtonActive = () => {
    if (visibleCount === null) return false;
    const activeIndex = navItems.findIndex(item => item.path === location.pathname);
    return activeIndex >= visibleCount && activeIndex !== -1;
  };

  const getLanguageLabel = () => {
    const langMap = {
      en: 'EN',
      de: 'DE',
      cs: 'CS'
    };
    return langMap[i18n.language] || 'EN';
  };

  const getAuthButtonText = () => {
    if (isLoading) return '...';
    if (isAuthenticated) {
      return user?.username || user?.email || t('header.logout');
    }
    return t('header.login');
  };

  const measuredWidthsRef = useRef([]);

  const calculateOverflow = useCallback(() => {
    if (window.innerWidth <= 1023) {
      setIsMobileMode(true);
      setVisibleCount(null);
      return;
    }

    setIsMobileMode(false);

    if (!headerContainerRef.current || !navRef.current) return;

    const headerContainer = headerContainerRef.current;
    const logo = headerContainer.querySelector('.header-logo');
    const actions = headerContainer.querySelector('.header-actions');

    if (!logo || !actions) return;

    const headerWidth = headerContainer.offsetWidth;
    const logoWidth = logo.offsetWidth;
    const actionsWidth = actions.offsetWidth;
    const padding = 64;
    const gap = 8;

    const availableWidth = headerWidth - logoWidth - actionsWidth - padding;

    const items = navItemsRef.current.filter(Boolean);
    const itemWidths = [];
    let totalWidth = 0;

    items.forEach((item, index) => {
      const width = item.offsetWidth + gap;
      itemWidths[index] = width;
      totalWidth += width;
    });

    measuredWidthsRef.current = itemWidths;

    if (totalWidth <= availableWidth) {
      setVisibleCount(null);
      return;
    }

    const moreButtonWidth = 100 + gap;
    const availableForItems = availableWidth - moreButtonWidth;

    let widthSum = 0;
    let count = 0;

    for (let i = 0; i < itemWidths.length; i++) {
      if (widthSum + itemWidths[i] <= availableForItems) {
        widthSum += itemWidths[i];
        count++;
      } else {
        break;
      }
    }

    count = Math.max(1, Math.min(count, navItems.length - 1));

    setVisibleCount(count);
  }, [navItems.length]);

  useEffect(() => {
    navItemsRef.current = navItemsRef.current.slice(0, navItems.length);

    const timeoutId = setTimeout(calculateOverflow, 0);

    const resizeObserver = new ResizeObserver(() => {
      calculateOverflow();
    });

    const mutationObserver = new MutationObserver(() => {
      setTimeout(calculateOverflow, 0);
    });

    if (headerContainerRef.current) {
      resizeObserver.observe(headerContainerRef.current);
    }

    if (navRef.current) {
      mutationObserver.observe(navRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    navItemsRef.current.forEach(item => {
      if (item) {
        resizeObserver.observe(item);
      }
    });

    window.addEventListener('resize', calculateOverflow);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', calculateOverflow);
    };
  }, [calculateOverflow, navItems.length]);

  useEffect(() => {
    const timeoutId = setTimeout(calculateOverflow, 100);
    return () => clearTimeout(timeoutId);
  }, [i18n.language, calculateOverflow]);

  useEffect(() => {
    const updateIndicator = () => {
      if (!navRef.current || isMobileMode) {
        setIndicatorStyle({ left: 0, width: 0 });
        return;
      }

      const isOnNavPage = navItems.some(item => item.path === location.pathname);

      if (!isOnNavPage) {
        setIndicatorStyle({ left: 0, width: 0 });
        return;
      }

      const activeIndex = navItems.findIndex(item => item.path === location.pathname);
      const isInOverflow = visibleCount !== null && activeIndex >= visibleCount;

      if (!isInOverflow) {
        const activeLink = navRef.current.querySelector('.header-nav-link.active:not(.overflow-hidden)');
        if (activeLink) {
          const navRect = navRef.current.getBoundingClientRect();
          const linkRect = activeLink.getBoundingClientRect();
          setIndicatorStyle({
            left: linkRect.left - navRect.left,
            width: linkRect.width,
          });
        }
      } else {
        const moreButton = navRef.current.querySelector('.more-button');
        if (moreButton) {
          const navRect = navRef.current.getBoundingClientRect();
          const moreRect = moreButton.getBoundingClientRect();
          setIndicatorStyle({
            left: moreRect.left - navRect.left,
            width: moreRect.width,
          });
        }
      }
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [location.pathname, visibleCount, isMobileMode, i18n.language, navItems]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langRef.current && !langRef.current.contains(event.target)) {
        setIsLangOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(event.target)) {
        setIsMoreOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const showMoreButton = !isMobileMode && visibleCount !== null && visibleCount < navItems.length;

  return (
    <header className="header">
      <div className="header-container" ref={headerContainerRef}>
        <Link to="/" className="header-logo">
          <img src="/svg/Star_of_David.svg" alt="" width="40" height="40" className="david-star" style={{ filter: 'invert(27%) sepia(98%) saturate(3024%) hue-rotate(205deg) brightness(94%) contrast(97%)' }} />
          <span className="header-logo-text">Židovská obec Teplice</span>
        </Link>

        <button
          className={`header-hamburger ${isMobileMode && isMenuOpen ? 'active' : ''} ${isMobileMode ? 'visible' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        <nav className={`header-nav ${isMobileMode && isMenuOpen ? 'active' : ''}`} ref={navRef}>
          {!isMobileMode && (
            <div
              className="nav-indicator"
              style={{
                transform: `translateX(${indicatorStyle.left}px)`,
                width: `${indicatorStyle.width}px`,
              }}
            />
          )}
          {navItems.map((item, index) => (
            <Link
              key={item.key}
              to={item.path}
              ref={(el) => (navItemsRef.current[index] = el)}
              className={`header-nav-link ${isActive(item.path) ? 'active' : ''} ${!isMobileMode && visibleCount !== null && index >= visibleCount ? 'overflow-hidden' : ''}`}
              onClick={() => {
                setIsMenuOpen(false);
                setIsMoreOpen(false);
              }}
            >
              {item.label}
            </Link>
          ))}

          {showMoreButton && (
            <div className="more-menu-wrapper" ref={moreRef}>
              <button
                ref={moreButtonRef}
                className={`header-nav-link more-button ${isMoreButtonActive() ? 'active' : ''}`}
                onClick={() => setIsMoreOpen(!isMoreOpen)}
                aria-label="More menu items"
              >
                {t('header.more')}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  className={`more-dropdown-arrow ${isMoreOpen ? 'open' : ''}`}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <div className={`more-dropdown ${isMoreOpen ? 'open' : ''}`}>
                {navItems.slice(visibleCount).map((item) => (
                  <Link
                    key={item.key}
                    to={item.path}
                    className={`more-dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                    onClick={() => {
                      setIsMoreOpen(false);
                      setIsMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {isMobileMode && (
            <div className="header-mobile-actions">
              <div className="language-switcher" ref={langRef}>
                <button
                  className="language-dropdown-btn"
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  aria-label="Change language"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                  <span>{getLanguageLabel()}</span>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    className={`dropdown-arrow ${isLangOpen ? 'open' : ''}`}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                <div className={`language-dropdown ${isLangOpen ? 'open' : ''}`}>
                  <button
                    className={`language-dropdown-item ${i18n.language === 'en' ? 'active' : ''}`}
                    onClick={() => changeLanguage('en')}
                  >
                    English
                  </button>
                  <button
                    className={`language-dropdown-item ${i18n.language === 'de' ? 'active' : ''}`}
                    onClick={() => changeLanguage('de')}
                  >
                    Deutsch
                  </button>
                  <button
                    className={`language-dropdown-item ${i18n.language === 'cs' ? 'active' : ''}`}
                    onClick={() => changeLanguage('cs')}
                  >
                    Čeština
                  </button>
                </div>
              </div>

              <button
                className="header-login-btn"
                onClick={handleAuthClick}
                disabled={isLoading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {getAuthButtonText()}
              </button>
            </div>
          )}
        </nav>

        <div className="header-actions header-desktop-actions">
          <div className="language-switcher" ref={langRef}>
            <button
              className="language-dropdown-btn"
              onClick={() => setIsLangOpen(!isLangOpen)}
              aria-label="Change language"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span>{getLanguageLabel()}</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className={`dropdown-arrow ${isLangOpen ? 'open' : ''}`}
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            <div className={`language-dropdown ${isLangOpen ? 'open' : ''}`}>
              <button
                className={`language-dropdown-item ${i18n.language === 'en' ? 'active' : ''}`}
                onClick={() => changeLanguage('en')}
              >
                English
              </button>
              <button
                className={`language-dropdown-item ${i18n.language === 'de' ? 'active' : ''}`}
                onClick={() => changeLanguage('de')}
              >
                Deutsch
              </button>
              <button
                className={`language-dropdown-item ${i18n.language === 'cs' ? 'active' : ''}`}
                onClick={() => changeLanguage('cs')}
              >
                Čeština
              </button>
            </div>
          </div>

          <button
            className="header-login-btn"
            onClick={handleAuthClick}
            disabled={isLoading}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {getAuthButtonText()}
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
